import React, {useState, useEffect} from 'react'
import {Box, Container, IconButton, Typography, styled, Modal} from '@mui/material'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import CloseIcon from '@mui/icons-material/Close'

interface TourStep {
  image: string
  text: string
  title?: string
  index?: number,
  fileKey?: string
}

interface TourGuideProps {
  steps: TourStep[]
  className?: string
}

// Styled components
const NavButton = styled(IconButton)(({theme}) => ({
  width: 48,
  height: 48,
  backgroundColor: '#F3F4FF',
  color: '#6366F1',
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  '&:hover': {
    backgroundColor: '#E8E9FF',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
  },
}))

const ImageContainer = styled(Box)({
  width: '100%',
  minHeight: '400px',
  maxHeight: '600px',
  overflow: 'hidden',
  borderRadius: 16,
  boxShadow: '10px 10px 20px 0 rgba(94,47,59,0.2)',
  position: 'relative',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
})

const StyledImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  transition: 'opacity 0.8s',
})

const ZoomModal = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const ZoomImageContainer = styled(Box)({
  position: 'relative',
  maxWidth: '90vw',
  maxHeight: '90vh',
  outline: 'none',
})

const ZoomedImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '90vh',
  objectFit: 'contain',
  backgroundColor: '#fff',
  padding: '20px',
  borderRadius: '8px',
})

const CloseButton = styled(IconButton)({
  position: 'absolute',
  top: 8,
  right: 8,
  color: '#FFFFFF',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
})

const TourGuide: React.FC<TourGuideProps> = ({steps}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [isZoomed, setIsZoomed] = useState(false)

  const showNextStep = () => {
    setCurrentStep((prev) => (prev < steps.length - 1 ? prev + 1 : 0))
  }

  const showPreviousStep = () => {
    setCurrentStep((prev) => (prev > 0 ? prev - 1 : steps.length - 1))
  }

  const handleImageClick = () => {
    setIsZoomed(true)
  }

  const handleCloseZoom = () => {
    setIsZoomed(false)
  }

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        showPreviousStep()
      } else if (e.key === 'ArrowRight') {
        showNextStep()
      } else if (e.key === 'Escape') {
        setIsZoomed(false)
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [])
  
  return (
    <Container maxWidth='md'>
      <Box sx={{position: 'relative', py: 4}}>
        {/* Slider */}
        <ImageContainer onClick={handleImageClick}>
          {steps.map((step, index) => (
            <StyledImage
              key={index}
              src={step.image}
              alt={step.title || `Step ${index + 1}`}
              sx={{
                display: index === currentStep ? 'block' : 'none',
                opacity: index === currentStep ? 1 : 0,
              }}
            />
          ))}
        </ImageContainer>

        {/* Zoom Modal */}
        <ZoomModal
          open={isZoomed}
          onClose={handleCloseZoom}
          aria-labelledby="zoom-modal"
          aria-describedby="zoom-modal-description"
        >
          <ZoomImageContainer>
            <ZoomedImage
              src={steps[currentStep].image}
              alt={steps[currentStep].title || `Step ${currentStep + 1}`}
            />
            <CloseButton onClick={handleCloseZoom}>
              <CloseIcon />
            </CloseButton>
          </ZoomImageContainer>
        </ZoomModal>

        {/* Content */}
        <Box sx={{mt: 4, textAlign: 'center'}}>
          {steps[currentStep].title && (
            <Typography variant='h4' component='h3' sx={{mb: 3, color: 'text.primary'}}>
              {`Step ${steps[currentStep]?.index} : ${steps[currentStep]?.title}`}
            </Typography>
          )}
          <Typography
            variant='body1'
            component='div'
            sx={{
              maxWidth: '42rem',
              mx: 'auto',
              color: 'text.secondary',
              fontSize: '1.325rem',
              lineHeight: 1.7,
            }}
            dangerouslySetInnerHTML={{__html: steps[currentStep].text}}
          />
        </Box>

        {/* Navigation */}
        <Box
          component='nav'
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <NavButton onClick={showPreviousStep} aria-label='Previous slide'>
            <ArrowBackIosNewIcon />
          </NavButton>
          <NavButton onClick={showNextStep} aria-label='Next slide'>
            <ArrowForwardIosIcon />
          </NavButton>
        </Box>
      </Box>
    </Container>
  )
}

export default TourGuide
